import { clsx } from 'clsx';
import { InputNumber } from 'antd';
import { useController } from 'react-hook-form';
import React, { JSX, useEffect, useState } from 'react';

import { moneyFormatter } from '@utils';
import { IFinancialTableRow } from '@types';
import cls from './FinancialRatios.module.css';


type FieldKeysType = 'totalAssets' | 'totalLiabilities' | 'stockholdersEquity' | 'debtToAssetRatio' |
  'debtToEquityRatio' | 'roe' | 'cashRatio' | 'dscr';

interface RowProps {
  key: FieldKeysType;
  title: string;
  value: number;
}

interface Props {
  readonly?: boolean;
}

export interface FinancialTableValue {
  key: string;
  title: string;
  value: number;
  percent?: number;
}

export const FinancialRatios = ({ readonly }: Props) => {
  const [financialRatiosDscr, setFinancialRatiosDscr] = useState<number | null>(null);
  const [
    financialRatiosStockholdersEquity, setFinancialRatiosStockholdersEquity,
  ] = useState<number | null>(null);
  const [
    financialRatiosTotalLiabilities, setFinancialRatiosTotalLiabilities,
  ] = useState<number | null>(null);
  const [financialRatiosTotalAssets, setFinancialRatiosTotalAssets] = useState<number | null>(null);
  const [
    financialRatiosDebtToAssetRatio, setFinancialRatiosDebtToAssetRatio,
  ] = useState<number | null>(null);
  const [
    financialRatiosDebtToEquityRatio, setFinancialRatiosDebtToEquityRatio,
  ] = useState<number | null>(null);
  const [financialRatiosROE, setFinancialRatiosROE] = useState<number | null>(null);
  const [financialRatiosCashRatio, setFinancialRatiosCashRatio] = useState<number | null>(null);
  const [calcData, setCalcData] = useState<IFinancialTableRow[][] | undefined>();
  const { field: { value: bureau } } = useController({ name: 'creditBureau' });
  const { field: { value: creditTerm } } = useController({ name: 'creditTerm' });
  const {
    field: { value: netProfit, onChange },
  } = useController({ name: 'financialRatiosNetProfit' });
  const {
    field: { onChange: modelSetFinancialRatiosDscr },
  } = useController({ name: 'financialRatiosDscr' });
  const {
    field: { onChange: modelSetFinancialRatiosStockholdersEquity },
  } = useController({ name: 'financialRatiosStockholdersEquity' });
  const {
    field: { onChange: modelSetTotalLiabilities },
  } = useController({ name: 'financialRatiosTotalLiabilities' });
  const {
    field: { onChange: modelSetTotalAssets },
  } = useController({ name: 'financialRatiosTotalAssets' });
  const {
    field: { onChange: modelSetDebtToAssetRatio },
  } = useController({ name: 'financialRatiosDebtToAssetRatio' });
  const {
    field: { onChange: modelSetDebtToEquityRatio },
  } = useController({ name: 'financialRatiosDebtToEquityRatio' });
  const {
    field: { onChange: modelSetROE },
  } = useController({ name: 'financialRatiosROE' });
  const {
    field: { onChange: modelSetCashRatio },
  } = useController({ name: 'financialRatiosCashRatio' });

  useEffect(() => {
    modelSetFinancialRatiosDscr(financialRatiosDscr || 0);
  }, [financialRatiosDscr]);

  useEffect(() => {
    modelSetTotalAssets(financialRatiosTotalAssets || 0);
  }, [financialRatiosTotalAssets]);

  useEffect(() => {
    modelSetTotalLiabilities(financialRatiosTotalLiabilities || 0);
  }, [financialRatiosTotalLiabilities]);

  useEffect(() => {
    modelSetFinancialRatiosStockholdersEquity(financialRatiosStockholdersEquity || 0);
  }, [financialRatiosStockholdersEquity]);

  useEffect(() => {
    modelSetDebtToAssetRatio(financialRatiosDebtToAssetRatio || 0);
  }, [financialRatiosDebtToAssetRatio]);

  useEffect(() => {
    modelSetDebtToEquityRatio(financialRatiosDebtToEquityRatio || 0);
  }, [financialRatiosDebtToEquityRatio]);

  useEffect(() => {
    modelSetROE(financialRatiosROE || 0);
  }, [financialRatiosROE]);

  useEffect(() => {
    modelSetCashRatio(financialRatiosCashRatio || 0);
  }, [financialRatiosCashRatio]);

  const handleChange = (val: number | null): void => {
    if (readonly) return;
    onChange(val || 0);
  };
  const totalPayment = bureau?.rows?.length
    ? bureau.rows.reduce((acc: number, r: any) => acc + (r.totalPayment || 0), 0)
    : 0;
  const capitalBalanceTotal = bureau?.rows?.length
    ? bureau.rows.reduce((acc: number, r: any) => acc + (r.capitalBalance || 0), 0)
    : 0;

  const getDSCR = (): number => {
    const creditTermValue = parseMonths(creditTerm);
    const monthlyNoi = bureau.monthlyNoi || 0;
    const guaranteeSuggestion = bureau.guarantee || 0;

    if (!monthlyNoi || !creditTermValue || !totalPayment) return 0;
    return (monthlyNoi * creditTermValue + guaranteeSuggestion * 0.5) / totalPayment;
  };

  const getFinancialRatios = (data: number): FinancialTableValue[][] => {
    const stockholdersEquity = data;
    setFinancialRatiosStockholdersEquity(stockholdersEquity);
    const totalLiabilities = capitalBalanceTotal;
    setFinancialRatiosTotalLiabilities(totalLiabilities);
    const totalAssets: number = totalLiabilities + stockholdersEquity;
    setFinancialRatiosTotalAssets(totalAssets);

    const debtToAssetRatio = Number((totalAssets ? totalLiabilities / totalAssets : 0)
      .toFixed(2));
    setFinancialRatiosDebtToAssetRatio(debtToAssetRatio);
    const debtToEquityRatio = Number((stockholdersEquity
      ? totalLiabilities / stockholdersEquity
      : 0).toFixed(2));
    setFinancialRatiosDebtToEquityRatio(debtToEquityRatio);
    const roe = Number((stockholdersEquity
      ? stockholdersEquity / stockholdersEquity
      : 0).toFixed(2));
    setFinancialRatiosROE(roe);
    const cashRatio = Number((totalLiabilities
      ? totalAssets / totalLiabilities
      : 0).toFixed(2));
    setFinancialRatiosCashRatio(cashRatio);

    const dscrField = Number(getDSCR().toFixed(2));
    setFinancialRatiosDscr(dscrField);

    const table1: FinancialTableValue[] = [
      { key: 'totalAssets', title: 'Total assets', value: totalAssets },
      { key: 'totalLiabilities', title: 'Total liabilities', value: totalLiabilities },
      { key: 'stockholdersEquity', title: 'Stockholders\' equity', value: stockholdersEquity },
    ];
    const table2: FinancialTableValue[] = [
      { key: 'debtToAssetRatio', title: 'Debt to Asset Ratio', value: debtToAssetRatio },
      { key: 'debtToEquityRatio', title: 'Debt to Equity Ratio', value: debtToEquityRatio },
      { key: 'roe', title: 'ROE', value: roe },
      { key: 'cashRatio', title: 'Cash Ratio', value: cashRatio },
      { key: 'dscr', title: 'DSCR', value: dscrField },
    ];

    return [table1, table2];
  };

  const getField = (i: IFinancialTableRow, isMoneyFields = false): JSX.Element => (
    <>
      <div className={clsx(cls.col, cls.colTitle)}>{i.title}</div>
      <div className={clsx(cls.col, cls.value)}>
        {isMoneyFields ? (
          <span className={cls.textWrap}>{moneyFormatter.format(i.value || 0)}</span>
        ) : (
          <span className={cls.textWrap}>
            {+(i.value || 0).toFixed(2)}
            {i.key === 'dscr' && +i.value ? 'x' : ''}
          </span>
        )}
      </div>
    </>
  );

  useEffect(() => {
    setCalcData(getFinancialRatios(netProfit || 0));
  }, [
    netProfit,
    creditTerm,
    bureau.totalPayment,
    bureau.monthlyNoi,
    bureau.guarantee,
  ]);

  return (!calcData || !calcData.length) ? null : (
    <div className={cls.tablesWrap}>
      <div className={cls.table}>
        {calcData[0].map(i => (
          <div className={cls.tableRow} key={i.key}>{getField(i, true)}</div>
        ))}
        <div className={cls.tableRow}>
          <div className={clsx(cls.col, cls.colTitle)}>Net profit</div>
          <div className={clsx(cls.col, cls.value, cls.colored)}>
            <span className={cls.inputWrap}>
              <InputNumber
                min={0}
                max={1000000000}
                formatter={(value) => `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value ? +value.replace(/\$\s?|(,*)/g, '') : 0}
                defaultValue={netProfit || 0}
                bordered={false}
                onChange={handleChange}
                className={cls.input}
                onBeforeInputCapture={readonly ? e => e.preventDefault() : undefined}
                disabled={readonly}
              />
            </span>
          </div>
        </div>
      </div>

      {calcData.length > 1 && calcData[1].length ? (
        <div className={cls.table}>
          {calcData[1].map(i => (
            <div className={cls.tableRow} key={i.key}>{getField(i)}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

function parseMonths(input: string): number {
  const match = String(input ?? '').trim().match(/^\s*(1[0-2]|[1-9])(?!\d)/);
  return match ? Number(match[1]) : 0;
}
